import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { ArchetypeCard } from "./ArchetypeCard";
import { GateCard } from "./GateCard";
import { AdhikaraCard } from "./AdhikaraCard";
import { PathwayCard } from "./PathwayCard";
import { DailyGuidance } from "./DailyGuidance";
import {
  fetchJourneyProfile,
  fetchJourneyToday,
  triggerJourneyRecompute,
} from "@/lib/sacred-journey/sacred-journey-api";

export function JourneyDashboard({ token }) {
  const [journey, setJourney] = useState(null);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [profile, todayRes] = await Promise.all([
        fetchJourneyProfile(token),
        fetchJourneyToday(token).catch(() => null),
      ]);
      setJourney(profile);
      setToday(todayRes);
    } catch {
      setJourney(null);
      setToday(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onRecompute = async () => {
    setRecomputing(true);
    try {
      await triggerJourneyRecompute(token);
      await load();
    } finally {
      setRecomputing(false);
    }
  };

  if (!token) return null;
  if (loading) {
    return (
      <div className="flex justify-center py-8 mb-8" data-testid="journey-dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!journey) return null;

  const guidance = today?.guidance ?? journey.daily_guidance;

  return (
    <section className="mb-8 space-y-4" data-testid="journey-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-xl">Your spiritual journey</h2>
        <Button type="button" size="sm" variant="outline" onClick={onRecompute} disabled={recomputing}>
          {recomputing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-1" />
          )}
          Refresh journey
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ArchetypeCard archetype={journey.archetype} />
        <GateCard gate={journey.karmic_gate} secondaryGate={journey.secondary_gate} />
        <AdhikaraCard adhikara={journey.adhikara_level} />
        <PathwayCard pathway={journey.pathway} secondaryPathways={journey.secondary_pathways} />
      </div>
      <DailyGuidance guidance={guidance} date={today?.date ?? guidance?.date} />
    </section>
  );
}
